import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { HelpLauncher } from "@/components/HelpCenter/HelpLauncher";

import { classNames } from "@/utils/classNames";

import { FeedSelectedList } from "@/pages/Private/redux/feed/feed.slice";

import { FeedListActions } from "./FeedListActions";
import { FeedListSwitcher } from "./FeedListSwitcher";

interface FeedTopbarProps {
	selectedLists: FeedSelectedList[];
	/** Whether the docked lead panel is minified to its slim right-edge strip. */
	isPanelCollapsed: boolean;
	onChangeSelectedLists: (lists: FeedSelectedList[]) => void;
}

/**
 * Topbar of the Feed page (`ListContextHeader` chrome, plan §6.1): page title +
 * the tracker-list multi-select switcher, with the help launcher on the right
 * (same affordance every `PageHeader` surface gets).
 *
 * The list-action cluster (PREC-3698) sits in the flexible middle rather than pinned right, so on
 * a narrow viewport it is what gives up width first and the help launcher stays put.
 *
 * The docked lead panel is a fixed right-edge overlay that paints over this bar,
 * so the right padding reserves the panel's width — otherwise the right-aligned
 * help launcher disappears underneath it. Same reservation as the run-card
 * column in `Feed.tsx` (450px panel / 35px strip + a ~24px gap), which keeps the
 * launcher aligned with the cards' right edge in both states.
 */
export const FeedTopbar: FunctionComponent<FeedTopbarProps> = ({
	selectedLists,
	isPanelCollapsed,
	onChangeSelectedLists,
}) => {
	const { t } = useTranslation();

	return (
		<div
			className={classNames(
				"flex items-center gap-2.5 py-2 px-4 bg-white border-b border-border-light",
				isPanelCollapsed ? "pr-[58px]" : "pr-[474px]"
			)}
		>
			<span className="text-sm font-semibold text-text whitespace-nowrap">{t("feed.title")}</span>

			<FeedListSwitcher selectedLists={selectedLists} onChange={onChangeSelectedLists} />

			<div className="flex-1 min-w-0 flex items-center justify-end overflow-hidden">
				<FeedListActions selectedLists={selectedLists} />
			</div>

			<HelpLauncher />
		</div>
	);
};
